import { useEffect, useState } from 'react';
import { ScoreDisclosure } from 'widgets/score-disclosure/score-disclosure';
import cls from './technical-accessibility.module.scss';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { ScoreCard } from 'components/score-card/score-card';
import { ScoreCardExpired } from 'components/score-card/score-card-expired';
import {
  techRuleFindingToInfoCompletenessScore,
  robosRuleToInfoCompletenessScore,
} from './helpers';
import { Card } from 'shared/ui/card/card';
import { ImprovementSectionHeader } from 'widgets/improvement-section-header/improvement-section-header';
import { RulesPurpose } from 'shared/ui/rules-purpose/rules-purpose';
import { Drawer } from 'shared/ui/drawer/drawer';
import { ImpactDrawerContent } from 'widgets/technical-rules/components/impact-drawer-content/impact-drawer-content';
import { ImprovementTrialBanner } from 'widgets/improvement-trial-banner/improvement-trial-banner';
import { useTechnicalAccessibilityState } from 'shared/hooks/useTechnicalAccessibilityState';
import { preloadTranslations } from 'services/translationService';
import { RULES_TECHNICAL_ACCESSIBILITY_TRANSLATION_FILE } from 'shared/constants/rules-texts';
import { TECHNICAL_ACCESSIBILITY_TOTAL_RULES } from 'shared/helpers/technicalAccessibility';
import { UnitSlot } from 'shared/ui/unit-slot/unit-slot';
import { RunBlockKey, useReportProgress } from 'shared/hooks/useReportProgress';
import { getSectionStatus } from 'shared/helpers/runSectionStatus';
import { RUN_BLOCK_EXPECTED_SECONDS } from 'shared/helpers/reportTimeline';

export interface TechnicalAccessibilityProps {
  showTitle?: boolean;
  className?: string;
  useImprovementVersion?: boolean;
}

export const TechnicalAccessibility: React.FC<TechnicalAccessibilityProps> = ({
  showTitle = false,
  className = '',
  useImprovementVersion = false,
}) => {
  const { t, currentLocale } = useTranslation();
  const { selectedProject, isTrialExpired } = useCachedSelectedProject();
  const [isImpactDrawerOpen, setIsImpactDrawerOpen] = useState(false);

  useEffect(() => {
    void preloadTranslations(currentLocale, [
      RULES_TECHNICAL_ACCESSIBILITY_TRANSLATION_FILE,
    ]);
  }, [currentLocale]);

  const {
    llmsRuleData,
    robotsRuleData: robotsTxtRuleData,
    schemaRuleData,
    llmsLoading: isLLMsLoading,
    robotsLoading: isRobotsLoading,
    schemaLoading: isSchemaLoading,
    technicalLoading,
    technicalStat,
  } = useTechnicalAccessibilityState();

  const showOnlyTotal =
    technicalStat.total < TECHNICAL_ACCESSIBILITY_TOTAL_RULES;

  const llmsScore = llmsRuleData
    ? techRuleFindingToInfoCompletenessScore(
        llmsRuleData.findings || {},
        'llms'
      )
    : {};
  const schemaScore = schemaRuleData
    ? techRuleFindingToInfoCompletenessScore(
        schemaRuleData.findings || {},
        'schema'
      )
    : {};
  const robotsTxtScoring = robotsTxtRuleData
    ? robosRuleToInfoCompletenessScore(robotsTxtRuleData)
    : {};

  const robotsDisclosureScore = robotsTxtRuleData?.score;
  const llmsDisclosureScore = llmsRuleData?.score;
  const schemaDisclosureScore = schemaRuleData?.score;

  const technicalAccessibilityScore =
    (llmsRuleData?.score ?? 0) * 0.25 +
    (schemaRuleData?.score ?? 0) * 0.3 +
    (robotsTxtRuleData?.score ?? 0) * 0.45;

  const formattedTechnicalAccessibilityScore = Number(
    technicalAccessibilityScore.toFixed(2)
  ).toString();

  const defaultIdentifiedState = {
    primary: 'Implemented',
    confidence_level: 'high',
    evidence_strength: 'strong',
  };

  const { blocks, blockStartedAt, isRunActive, allComplete } =
    useReportProgress();

  const renderRuleSlot = (
    block: RunBlockKey,
    labelKey: string,
    content: React.ReactNode
  ) =>
    isRunActive ? (
      <UnitSlot
        unitState={blocks[block]}
        name={t(labelKey)}
        minHeight={76}
        className={useImprovementVersion ? cls.attributeSlot : undefined}
        startedAt={blockStartedAt[block]}
        expectedSeconds={RUN_BLOCK_EXPECTED_SECONDS[block]}
      >
        {content}
      </UnitSlot>
    ) : (
      content
    );

  if (!selectedProject) {
    if (useImprovementVersion) {
      return (
        <Card className={cls.accessibilityCard}>
          <Skeleton height={60} />
          <div className={cls.accessibilityContainer}>
            <div className={cls.rulesSection}>
              <div className={cls.skeletonWrapper}>
                <Skeleton height={44} />
              </div>
              <div className={cls.skeletonWrapper}>
                <Skeleton height={44} />
              </div>
              <div className={cls.skeletonWrapper}>
                <Skeleton height={44} />
              </div>
            </div>
            <div className={cls.purposeSection}>
              <Skeleton height={300} />
            </div>
          </div>
        </Card>
      );
    }

    return (
      <div className={`${cls.pageContent} ${className}`}>
        <div className={cls.scoreDisclosure}>
          <Skeleton height={76} />
          <Skeleton height={76} />
          <Skeleton height={76} />
        </div>
        <Skeleton height={240} width={356} />
      </div>
    );
  }

  const renderRules = (isImprovement: boolean) => {
    const itemClassName = isImprovement ? cls.disclosureItem : undefined;
    const buttonClass = isImprovement ? cls.disclosureButton : undefined;
    const contentClass = isImprovement ? cls.disclosureContent : undefined;
    return (
      <>
        {renderRuleSlot(
          'robotsTxt',
          'robots_txt_permissions',
          <ScoreDisclosure
            score={robotsDisclosureScore}
            title={'robots_txt_permissions'}
            description={t('technicalAccessibilityRobotsDescription')}
            scoring={robotsTxtScoring}
            confidence='high'
            evidence='strong'
            identified={defaultIdentifiedState}
            urlsAnalyzed={[]}
            isLoading={!isRunActive && isRobotsLoading}
            variant='technical-accessibility'
            ruleDescription={t('technicalAccessibilityRobotsRuleDescription')}
            className={itemClassName}
            buttonClassName={buttonClass}
            contentClassName={contentClass}
          />
        )}
        {renderRuleSlot(
          'llmsTxt',
          'llms_txt_configuration',
          <ScoreDisclosure
            score={llmsDisclosureScore}
            title={'llms_txt_configuration'}
            description={t('technicalAccessibilityLlmsDescription')}
            scoring={llmsScore}
            confidence='high'
            evidence='strong'
            identified={defaultIdentifiedState}
            urlsAnalyzed={[]}
            isLoading={!isRunActive && isLLMsLoading}
            variant='technical-accessibility'
            ruleDescription={t('technicalAccessibilityLlmsRuleDescription')}
            className={itemClassName}
            buttonClassName={buttonClass}
            contentClassName={contentClass}
          />
        )}
        {renderRuleSlot(
          'schemaOrg',
          'structured_data',
          <ScoreDisclosure
            score={schemaDisclosureScore}
            title={'structured_data'}
            description={t('technicalAccessibilitySchemaDescription')}
            scoring={schemaScore}
            confidence='high'
            evidence='strong'
            identified={defaultIdentifiedState}
            urlsAnalyzed={[]}
            isLoading={!isRunActive && isSchemaLoading}
            variant='technical-accessibility'
            ruleDescription={t('technicalAccessibilitySchemaRuleDescription')}
            className={itemClassName}
            buttonClassName={buttonClass}
            contentClassName={contentClass}
          />
        )}
        {/* TODO: Uncomment SSR rule when backend is ready
        {technicalLoading ? (
          isImprovement ? (
            <div className={cls.skeletonWrapper}>
              <Skeleton height={44} />
            </div>
          ) : (
            <Skeleton height={76} />
          )
        ) : (
          <ScoreDisclosure
            score={0}
            title={'server_side_rendering'}
            description={t('technicalAccessibilitySsrDescription')}
            scoring={{}}
            confidence='high'
            evidence='strong'
            identified={defaultIdentifiedState}
            urlsAnalyzed={[]}
            isLoading={false}
            variant='technical-accessibility'
            ruleDescription={t('technicalAccessibilitySsrRuleDescription')}
            className={itemClassName}
            buttonClassName={buttonClass}
            contentClassName={contentClass}
          />
        )}
        */}
      </>
    );
  };

  if (useImprovementVersion) {
    return (
      <Card className={cls.accessibilityCard}>
        <ImprovementSectionHeader
          title={t('technicalAccessibility')}
          stats={technicalStat}
          isLoading={technicalLoading}
          isTrialExpired={isTrialExpired}
          showOnlyTotal={showOnlyTotal}
          totalOverride={TECHNICAL_ACCESSIBILITY_TOTAL_RULES}
          runStatus={
            isRunActive
              ? getSectionStatus([
                  blocks.robotsTxt,
                  blocks.llmsTxt,
                  blocks.schemaOrg,
                ])
              : null
          }
        />

        <div className={cls.accessibilityContainer}>
          <div className={cls.rulesSection}>
            <div className={cls.rulesList}>{renderRules(true)}</div>
            <ImprovementTrialBanner />
          </div>

          <div className={cls.purposeSection}>
            <RulesPurpose
              purposeText={t('technicalRequirementsAnalysisDescription')}
              implementedText={t('technicalRequirementsAnalysisImplemented')}
              notImplementedText={t(
                'technicalRequirementsAnalysisNotImplemented'
              )}
              onViewFullImpact={() => setIsImpactDrawerOpen(true)}
              showViewFullImpact={!isRunActive || allComplete}
            />
          </div>
        </div>

        <Drawer
          isOpen={isImpactDrawerOpen}
          onClose={() => setIsImpactDrawerOpen(false)}
          title={`${t('technicalAccessibility')} ${t('fullImpact')}`}
        >
          <ImpactDrawerContent />
        </Drawer>
      </Card>
    );
  }

  return (
    <div className={`${cls.pageContent} ${className}`}>
      <div className={cls.info}>{renderRules(false)}</div>
      {isTrialExpired ? (
        <ScoreCardExpired
          showTitle={showTitle}
          className={cls.scoreCard}
          size='small'
          title='technicalAccessibility'
        />
      ) : (
        <ScoreCard
          showTitle={showTitle}
          score={technicalAccessibilityScore}
          displayScore={formattedTechnicalAccessibilityScore}
          className={cls.scoreCard}
          size='small'
          title='technicalAccessibility'
          trackName='TECHNICAL_ACCESSIBILITY'
          isLoading={technicalLoading}
        />
      )}
    </div>
  );
};
